import { useRef, useCallback } from 'react';

/**
 * Hook para gerenciar cache de thumbnails com limite LRU (Least Recently Used)
 * Solução 4: Cache limitado para evitar memory leaks
 */
export function useThumbnailCache(maxSize = 50) {
  const cacheRef = useRef(new Map());
  const accessTimeRef = useRef(new Map());

  /**
   * Obtém um thumbnail do cache
   * @param {string} key - Chave do thumbnail
   * @returns {string|null} - URL do thumbnail ou null se não existe
   */
  const get = useCallback((key) => {
    const entry = cacheRef.current.get(key);
    if (entry) {
      // Atualizar tempo de acesso (LRU)
      accessTimeRef.current.set(key, Date.now());
      return entry;
    }
    return null;
  }, []);

  /**
   * Adiciona ou atualiza um thumbnail no cache
   * Remove entradas mais antigas se o cache exceder o limite
   * @param {string} key - Chave do thumbnail
   * @param {string} value - URL do thumbnail
   */
  const set = useCallback((key, value) => {
    // Se já existe, apenas atualizar tempo de acesso
    if (cacheRef.current.has(key)) {
      accessTimeRef.current.set(key, Date.now());
      cacheRef.current.set(key, value);
      return;
    }

    // Se cache está cheio, remover menos usado recentemente
    if (cacheRef.current.size >= maxSize) {
      // Ordenar por tempo de acesso (menos recente primeiro)
      const sortedByAccess = Array.from(accessTimeRef.current.entries())
        .sort((a, b) => a[1] - b[1]);
      
      // Remover 10% das entradas mais antigas (ou pelo menos 1)
      const toRemoveCount = Math.max(1, Math.floor(maxSize * 0.1));
      const toRemove = sortedByAccess.slice(0, toRemoveCount);
      
      toRemove.forEach(([keyToRemove]) => {
        cacheRef.current.delete(keyToRemove);
        accessTimeRef.current.delete(keyToRemove);
      });
      
      console.log(`🧹 [ThumbnailCache] Removidas ${toRemoveCount} entradas antigas do cache`);
    }

    // Adicionar nova entrada
    cacheRef.current.set(key, value);
    accessTimeRef.current.set(key, Date.now());
    
    console.log(`💾 [ThumbnailCache] Cache size: ${cacheRef.current.size}/${maxSize}`);
  }, [maxSize]);

  /**
   * Remove uma entrada específica do cache
   * @param {string} key - Chave do thumbnail
   */
  const remove = useCallback((key) => {
    cacheRef.current.delete(key);
    accessTimeRef.current.delete(key);
  }, []);

  /**
   * Limpa todo o cache
   */
  const clear = useCallback(() => {
    const size = cacheRef.current.size;
    cacheRef.current.clear();
    accessTimeRef.current.clear();
    console.log(`🗑️ [ThumbnailCache] Cache limpo (${size} entradas removidas)`);
  }, []);

  /**
   * Retorna estatísticas do cache
   */
  const getStats = useCallback(() => {
    return {
      size: cacheRef.current.size,
      maxSize,
      usage: Math.round((cacheRef.current.size / maxSize) * 100),
      oldestAccess: accessTimeRef.current.size > 0 
        ? Math.min(...Array.from(accessTimeRef.current.values()))
        : null,
      newestAccess: accessTimeRef.current.size > 0
        ? Math.max(...Array.from(accessTimeRef.current.values()))
        : null
    };
  }, [maxSize]);

  return {
    get,
    set,
    remove,
    clear,
    getStats
  };
}

